import {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Stack,
  TextInput,
  Textarea,
  Group,
  Text,
  Accordion,
  Switch,
  Box,
  Badge,
  SimpleGrid,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  IconStethoscope,
  IconUser,
  IconHeart,
  IconCircle,
  IconWalk,
  IconBrain,
  IconCheck,
} from '@tabler/icons-react';
import {
  type PhysicalExaminationData,
  type PhysicalExaminationInputProps,
} from './types';
import {
  createEmptyPhysicalExamination,
  sectionHasContent,
} from './physical-examination.utils';

// Maximum length for clinical notes in textareas
const CLINICAL_NOTE_MAX_LENGTH = 2000;

// Clinical ranges for vital signs validation
const VITAL_SIGNS_RANGES = {
  bloodPressureSystolic: { min: 60, max: 300, unit: 'mmHg' },
  bloodPressureDiastolic: { min: 30, max: 200, unit: 'mmHg' },
  heartRate: { min: 20, max: 300, unit: 'bpm' },
  respiratoryRate: { min: 4, max: 80, unit: '/min' },
  temperature: { min: 25, max: 45, unit: '°C' },
  oxygenSaturation: { min: 50, max: 100, unit: '%' },
} as const;

type VitalSignField = keyof typeof VITAL_SIGNS_RANGES;

// Ref type for imperative access to form data
export interface PhysicalExaminationInputRef {
  getData: () => PhysicalExaminationData;
}

export const PhysicalExaminationInput = forwardRef<
  PhysicalExaminationInputRef,
  PhysicalExaminationInputProps
>(({ onChange, initialData }, ref) => {
  const { t } = useTranslation(['patient-pages']);

  const [data, setData] = useState<PhysicalExaminationData>(() => {
    return initialData || createEmptyPhysicalExamination();
  });

  const [vitalSignErrors, setVitalSignErrors] = useState<
    Partial<Record<VitalSignField, string>>
  >({});

  const prevInitialDataRef = useRef<string>('');

  // Expose getData method via ref - bypasses debounce for immediate access
  useImperativeHandle(
    ref,
    () => ({
      getData: () => data,
    }),
    [data]
  );

  // Validate a vital sign value against its clinical range
  const validateVitalSign = useCallback(
    (
      field: VitalSignField,
      value: string,
      allVitalSigns: PhysicalExaminationData['vitalSigns']
    ): string | undefined => {
      if (!value || value.trim() === '') {
        return undefined;
      }

      const numValue = Number(value);
      if (isNaN(numValue)) {
        return t('examination.physicalExamination.validation.invalidNumber', {
          ns: 'patient-pages',
          defaultValue: 'Please enter a valid number',
        });
      }

      const range = VITAL_SIGNS_RANGES[field];
      if (numValue < range.min || numValue > range.max) {
        return t('examination.physicalExamination.validation.outOfRange', {
          ns: 'patient-pages',
          defaultValue: 'Value must be between {{min}} and {{max}} {{unit}}',
          min: range.min,
          max: range.max,
          unit: range.unit,
        });
      }

      // Cross-field validation for blood pressure
      if (field === 'bloodPressureDiastolic') {
        const systolic = Number(allVitalSigns.bloodPressureSystolic);
        if (
          !isNaN(systolic) &&
          allVitalSigns.bloodPressureSystolic &&
          numValue >= systolic
        ) {
          return t(
            'examination.physicalExamination.validation.diastolicLessThanSystolic',
            {
              ns: 'patient-pages',
              defaultValue: 'Diastolic must be less than systolic',
            }
          );
        }
      }

      if (field === 'bloodPressureSystolic') {
        const diastolic = Number(allVitalSigns.bloodPressureDiastolic);
        if (
          !isNaN(diastolic) &&
          allVitalSigns.bloodPressureDiastolic &&
          numValue <= diastolic
        ) {
          return t(
            'examination.physicalExamination.validation.systolicGreaterThanDiastolic',
            {
              ns: 'patient-pages',
              defaultValue: 'Systolic must be greater than diastolic',
            }
          );
        }
      }

      return undefined;
    },
    [t]
  );

  // Debounce the data changes before calling onChange
  const [debouncedData] = useDebouncedValue(data, 300);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    const currentSerialized = JSON.stringify(initialData);
    if (currentSerialized !== prevInitialDataRef.current) {
      setData(initialData || createEmptyPhysicalExamination());
      prevInitialDataRef.current = currentSerialized;
      isFirstRenderRef.current = true; // Reset on new initial data
    }
  }, [initialData]);

  // Call onChange with debounced data
  useEffect(() => {
    // Skip the first render to avoid calling onChange on mount
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    onChange?.(debouncedData);
  }, [debouncedData, onChange]);

  const updateVitalSigns = (
    field: keyof PhysicalExaminationData['vitalSigns'],
    value: string
  ) => {
    const updatedVitalSigns = { ...data.vitalSigns, [field]: value };
    const updated = {
      ...data,
      vitalSigns: updatedVitalSigns,
    };
    setData(updated);

    // Validate the field
    const error = validateVitalSign(
      field as VitalSignField,
      value,
      updatedVitalSigns
    );
    setVitalSignErrors((prev) => {
      const newErrors = { ...prev, [field]: error };
      // Also revalidate cross-field validation for blood pressure
      if (
        field === 'bloodPressureSystolic' &&
        updatedVitalSigns.bloodPressureDiastolic
      ) {
        newErrors.bloodPressureDiastolic = validateVitalSign(
          'bloodPressureDiastolic',
          updatedVitalSigns.bloodPressureDiastolic,
          updatedVitalSigns
        );
      }
      if (
        field === 'bloodPressureDiastolic' &&
        updatedVitalSigns.bloodPressureSystolic
      ) {
        newErrors.bloodPressureSystolic = validateVitalSign(
          'bloodPressureSystolic',
          updatedVitalSigns.bloodPressureSystolic,
          updatedVitalSigns
        );
      }
      return newErrors;
    });
    // onChange is called via the debounced useEffect
  };

  const updateSection = <
    K extends keyof Omit<PhysicalExaminationData, 'vitalSigns'>,
  >(
    section: K,
    field: keyof PhysicalExaminationData[K],
    value: string | boolean
  ) => {
    const updated = {
      ...data,
      [section]: { ...data[section], [field]: value },
    };
    setData(updated);
    // onChange is called via the debounced useEffect
  };

  const renderSectionHeader = (
    title: string,
    section: keyof PhysicalExaminationData
  ) => {
    const sectionData = data[section];
    const isNormal = 'normal' in sectionData && sectionData.normal;
    const hasContent = sectionHasContent(
      sectionData as Record<string, string | boolean>
    );
    const showToggle = section !== 'vitalSigns'; // No toggle for vitals

    // Check vitals
    let vitalsHasContent = false;
    if (section === 'vitalSigns') {
      const v = data.vitalSigns;
      vitalsHasContent = !!(
        v.bloodPressureSystolic ||
        v.bloodPressureDiastolic ||
        v.heartRate ||
        v.respiratoryRate ||
        v.temperature ||
        v.oxygenSaturation
      );
    }

    const isComplete = isNormal || hasContent || vitalsHasContent;

    return (
      <Group gap="xs" justify="space-between" pr="xs">
        <Group gap="xs">
          <Text>{title}</Text>
          {isComplete && !showToggle && (
            <Badge size="sm" variant="light" color="blue">
              <IconCheck size={12} />
            </Badge>
          )}
        </Group>
        {showToggle && (
          <Group
            gap="xs"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Text size="xs" c="dimmed">
              {t('examination.physicalExamination.wnl', {
                ns: 'patient-pages',
                defaultValue: 'WNL',
              })}
            </Text>
            <Switch
              size="xs"
              checked={isNormal}
              onChange={(e) => {
                e.stopPropagation();
                updateSection(
                  section as keyof Omit<PhysicalExaminationData, 'vitalSigns'>,
                  'normal' as never,
                  e.currentTarget.checked
                );
              }}
              color="green"
            />
          </Group>
        )}
      </Group>
    );
  };

  const renderTextareas = (
    section: keyof Omit<PhysicalExaminationData, 'vitalSigns'>,
    fields: { key: string; label: string }[]
  ) => {
    const sectionData = data[section];
    const isNormal = sectionData.normal ?? false;

    return (
      <Box opacity={isNormal ? 0.5 : 1}>
        <Stack gap="md">
          {fields.map((field) => (
            <Textarea
              key={field.key}
              label={field.label}
              value={
                ((sectionData as Record<string, unknown>)[
                  field.key
                ] as string) || ''
              }
              onChange={(e) =>
                updateSection(
                  section,
                  field.key as keyof PhysicalExaminationData[typeof section],
                  e.currentTarget.value
                )
              }
              autosize
              minRows={2}
              maxLength={CLINICAL_NOTE_MAX_LENGTH}
              disabled={isNormal}
              placeholder={
                isNormal
                  ? t('examination.physicalExamination.withinNormalLimits', {
                      ns: 'patient-pages',
                      defaultValue: 'No pathological findings',
                    })
                  : undefined
              }
            />
          ))}
        </Stack>
      </Box>
    );
  };

  return (
    <Accordion variant="separated" multiple defaultValue={['vitalSigns']}>
      {/* Vital Signs */}
      <Accordion.Item value="vitalSigns">
        <Accordion.Control icon={<IconStethoscope size={20} />}>
          {renderSectionHeader(
            t('examination.physicalExamination.vitalSigns.title', {
              ns: 'patient-pages',
              defaultValue: 'Vital Signs',
            }),
            'vitalSigns'
          )}
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="md">
            <SimpleGrid cols={3} spacing="md">
              <TextInput
                label={t(
                  'examination.physicalExamination.vitalSigns.bloodPressureSystolic',
                  { ns: 'patient-pages', defaultValue: 'Systolic BP' }
                )}
                placeholder="120"
                type="number"
                min="0"
                max="300"
                value={data.vitalSigns.bloodPressureSystolic}
                onChange={(e) =>
                  updateVitalSigns(
                    'bloodPressureSystolic',
                    e.currentTarget.value
                  )
                }
                error={vitalSignErrors.bloodPressureSystolic}
                rightSection={
                  <Text size="sm" c="dimmed">
                    /
                  </Text>
                }
              />
              <TextInput
                label={t(
                  'examination.physicalExamination.vitalSigns.bloodPressureDiastolic',
                  { ns: 'patient-pages', defaultValue: 'Diastolic BP' }
                )}
                placeholder="80"
                type="number"
                min="0"
                max="200"
                value={data.vitalSigns.bloodPressureDiastolic}
                onChange={(e) =>
                  updateVitalSigns(
                    'bloodPressureDiastolic',
                    e.currentTarget.value
                  )
                }
                error={vitalSignErrors.bloodPressureDiastolic}
                rightSection={
                  <Text size="sm" c="dimmed" ml={-13}>
                    mmHg
                  </Text>
                }
              />
              <TextInput
                label={t(
                  'examination.physicalExamination.vitalSigns.heartRate',
                  {
                    ns: 'patient-pages',
                    defaultValue: 'Heart Rate',
                  }
                )}
                type="number"
                min="0"
                value={data.vitalSigns.heartRate}
                onChange={(e) =>
                  updateVitalSigns('heartRate', e.currentTarget.value)
                }
                error={vitalSignErrors.heartRate}
                rightSection={
                  <Text size="sm" c="dimmed">
                    bpm
                  </Text>
                }
              />

              <TextInput
                label={t(
                  'examination.physicalExamination.vitalSigns.respiratoryRate',
                  { ns: 'patient-pages', defaultValue: 'Respiratory Rate' }
                )}
                type="number"
                min="0"
                value={data.vitalSigns.respiratoryRate}
                onChange={(e) =>
                  updateVitalSigns('respiratoryRate', e.currentTarget.value)
                }
                error={vitalSignErrors.respiratoryRate}
                rightSection={
                  <Text size="sm" c="dimmed">
                    /min
                  </Text>
                }
              />
              <TextInput
                label={t(
                  'examination.physicalExamination.vitalSigns.temperature',
                  {
                    ns: 'patient-pages',
                    defaultValue: 'Temperature',
                  }
                )}
                type="number"
                min="0"
                step="0.1"
                value={data.vitalSigns.temperature}
                onChange={(e) =>
                  updateVitalSigns('temperature', e.currentTarget.value)
                }
                error={vitalSignErrors.temperature}
                rightSection={
                  <Text size="sm" c="dimmed">
                    °C
                  </Text>
                }
              />
              <TextInput
                label={t(
                  'examination.physicalExamination.vitalSigns.oxygenSaturation',
                  { ns: 'patient-pages', defaultValue: 'SpO2' }
                )}
                type="number"
                min="0"
                max="100"
                value={data.vitalSigns.oxygenSaturation}
                onChange={(e) =>
                  updateVitalSigns('oxygenSaturation', e.currentTarget.value)
                }
                error={vitalSignErrors.oxygenSaturation}
                rightSection={
                  <Text size="sm" c="dimmed">
                    %
                  </Text>
                }
              />
            </SimpleGrid>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>

      {/* Head & Neck */}
      <Accordion.Item value="headNeck">
        <Accordion.Control icon={<IconUser size={20} />}>
          {renderSectionHeader(
            t('examination.physicalExamination.headNeck.title', {
              ns: 'patient-pages',
              defaultValue: 'Head & Neck',
            }),
            'headNeck'
          )}
        </Accordion.Control>
        <Accordion.Panel>
          {renderTextareas('headNeck', [
            {
              key: 'cranium',
              label: t('examination.physicalExamination.headNeck.cranium', {
                ns: 'patient-pages',
                defaultValue: 'Cranium',
              }),
            },
            {
              key: 'eyes',
              label: t('examination.physicalExamination.headNeck.eyes', {
                ns: 'patient-pages',
                defaultValue: 'Eyes',
              }),
            },
            {
              key: 'ent',
              label: t('examination.physicalExamination.headNeck.ent', {
                ns: 'patient-pages',
                defaultValue: 'ENT',
              }),
            },
            {
              key: 'neck',
              label: t('examination.physicalExamination.headNeck.neck', {
                ns: 'patient-pages',
                defaultValue: 'Neck',
              }),
            },
          ])}
        </Accordion.Panel>
      </Accordion.Item>

      {/* Thorax */}
      <Accordion.Item value="thorax">
        <Accordion.Control icon={<IconHeart size={20} />}>
          {renderSectionHeader(
            t('examination.physicalExamination.thorax.title', {
              ns: 'patient-pages',
              defaultValue: 'Thorax',
            }),
            'thorax'
          )}
        </Accordion.Control>
        <Accordion.Panel>
          {renderTextareas('thorax', [
            {
              key: 'respiratory',
              label: t('examination.physicalExamination.thorax.respiratory', {
                ns: 'patient-pages',
                defaultValue: 'Respiratory',
              }),
            },
            {
              key: 'cardiovascular',
              label: t(
                'examination.physicalExamination.thorax.cardiovascular',
                {
                  ns: 'patient-pages',
                  defaultValue: 'Cardiovascular',
                }
              ),
            },
          ])}
        </Accordion.Panel>
      </Accordion.Item>

      {/* Abdomen */}
      <Accordion.Item value="abdomen">
        <Accordion.Control icon={<IconCircle size={20} />}>
          {renderSectionHeader(
            t('examination.physicalExamination.abdomen.title', {
              ns: 'patient-pages',
              defaultValue: 'Abdomen',
            }),
            'abdomen'
          )}
        </Accordion.Control>
        <Accordion.Panel>
          {renderTextareas('abdomen', [
            {
              key: 'inspection',
              label: t('examination.physicalExamination.abdomen.inspection', {
                ns: 'patient-pages',
                defaultValue: 'Inspection',
              }),
            },
            {
              key: 'auscultation',
              label: t('examination.physicalExamination.abdomen.auscultation', {
                ns: 'patient-pages',
                defaultValue: 'Auscultation',
              }),
            },
            {
              key: 'palpation',
              label: t('examination.physicalExamination.abdomen.palpation', {
                ns: 'patient-pages',
                defaultValue: 'Palpation',
              }),
            },
            {
              key: 'percussion',
              label: t('examination.physicalExamination.abdomen.percussion', {
                ns: 'patient-pages',
                defaultValue: 'Percussion',
              }),
            },
          ])}
        </Accordion.Panel>
      </Accordion.Item>

      {/* Extremities */}
      <Accordion.Item value="extremities">
        <Accordion.Control icon={<IconWalk size={20} />}>
          {renderSectionHeader(
            t('examination.physicalExamination.extremities.title', {
              ns: 'patient-pages',
              defaultValue: 'Extremities',
            }),
            'extremities'
          )}
        </Accordion.Control>
        <Accordion.Panel>
          {renderTextareas('extremities', [
            {
              key: 'inspection',
              label: t(
                'examination.physicalExamination.extremities.inspection',
                {
                  ns: 'patient-pages',
                  defaultValue: 'Inspection',
                }
              ),
            },
            {
              key: 'edema',
              label: t('examination.physicalExamination.extremities.edema', {
                ns: 'patient-pages',
                defaultValue: 'Edema',
              }),
            },
            {
              key: 'skinHair',
              label: t('examination.physicalExamination.extremities.skinHair', {
                ns: 'patient-pages',
                defaultValue: 'Skin & Hair',
              }),
            },
            {
              key: 'pulses',
              label: t('examination.physicalExamination.extremities.pulses', {
                ns: 'patient-pages',
                defaultValue: 'Peripheral Pulses',
              }),
            },
          ])}
        </Accordion.Panel>
      </Accordion.Item>

      {/* Neurological */}
      <Accordion.Item value="neurological">
        <Accordion.Control icon={<IconBrain size={20} />}>
          {renderSectionHeader(
            t('examination.physicalExamination.neurological.title', {
              ns: 'patient-pages',
              defaultValue: 'Neurological',
            }),
            'neurological'
          )}
        </Accordion.Control>
        <Accordion.Panel>
          {renderTextareas('neurological', [
            {
              key: 'alertnessOrientation',
              label: t(
                'examination.physicalExamination.neurological.alertnessOrientation',
                { ns: 'patient-pages', defaultValue: 'Alertness & Orientation' }
              ),
            },
            {
              key: 'speech',
              label: t('examination.physicalExamination.neurological.speech', {
                ns: 'patient-pages',
                defaultValue: 'Speech',
              }),
            },
            {
              key: 'cranialNerves',
              label: t(
                'examination.physicalExamination.neurological.cranialNerves',
                { ns: 'patient-pages', defaultValue: 'Cranial Nerves' }
              ),
            },
            {
              key: 'muscleStrength',
              label: t(
                'examination.physicalExamination.neurological.muscleStrength',
                { ns: 'patient-pages', defaultValue: 'Muscle Strength' }
              ),
            },
            {
              key: 'sensation',
              label: t(
                'examination.physicalExamination.neurological.sensation',
                {
                  ns: 'patient-pages',
                  defaultValue: 'Sensation',
                }
              ),
            },
            {
              key: 'gaitBalance',
              label: t(
                'examination.physicalExamination.neurological.gaitBalance',
                { ns: 'patient-pages', defaultValue: 'Gait & Balance' }
              ),
            },
            {
              key: 'cerebellarSigns',
              label: t(
                'examination.physicalExamination.neurological.cerebellarSigns',
                { ns: 'patient-pages', defaultValue: 'Cerebellar Signs' }
              ),
            },
          ])}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
});

export default PhysicalExaminationInput;
